import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { trpc } from "@/lib/trpc";
import { zodResolver } from "@hookform/resolvers/zod";
import { Building2, Trophy, UserRound } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useLocation } from "wouter";
import { z } from "zod";
import { useTranslation, Trans } from "react-i18next";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

// Почта поддержки для незалогиненных (страница /support требует входа).
const SUPPORT_EMAIL = "judixkz@gmail.com";

const roleValues = ["school", "organizer", "coach"] as const;
type RoleValue = (typeof roleValues)[number];

// Тексты ролей берутся из i18n при рендере (register.roles.<value>.*).
const roleCards = [
  { value: "school", icon: Building2 },
  { value: "coach", icon: UserRound },
  { value: "organizer", icon: Trophy },
] as const;

// Сообщения схемы — ключи i18n (namespace auth), переводятся при рендере.
const registerSchema = z
  .object({
    role: z.enum(roleValues).optional(),
    schoolName: z.string().optional(),
    orgName: z.string().optional(),
    fullName: z.string().optional(),
    coachSchoolId: z.string().optional(),
    newSchoolName: z.string().optional(),
    newSchoolCity: z.string().optional(),
    phone: z.string().min(1, "errors.phoneRequired"),
    email: z.string().email("errors.emailInvalid"),
    password: z.string().min(6, "errors.passwordMin"),
    passwordConfirm: z.string().min(6, "errors.passwordConfirmMin"),
  })
  .refine(values => values.password === values.passwordConfirm, {
    message: "errors.passwordMismatch",
    path: ["passwordConfirm"],
  })
  .superRefine((values, ctx) => {
    if (!values.role) {
      ctx.addIssue({ code: "custom", message: "errors.roleRequired", path: ["role"] });
      return;
    }

    const phoneDigits = values.phone.replace(/\D/g, "");
    if (phoneDigits.length < 6) {
      ctx.addIssue({ code: "custom", message: "errors.phoneRequired", path: ["phone"] });
    }

    if (values.role === "school" && !values.schoolName?.trim()) {
      ctx.addIssue({ code: "custom", message: "errors.schoolNameRequired", path: ["schoolName"] });
    }
    if (values.role === "organizer" && !values.orgName?.trim()) {
      ctx.addIssue({ code: "custom", message: "errors.orgNameRequired", path: ["orgName"] });
    }
    if (values.role === "coach") {
      if (!values.fullName?.trim()) {
        ctx.addIssue({ code: "custom", message: "errors.fullNameRequired", path: ["fullName"] });
      }
      // либо выбрана школа из списка, либо указано название новой
      if (!values.coachSchoolId && !values.newSchoolName?.trim()) {
        ctx.addIssue({ code: "custom", message: "errors.schoolRequired", path: ["coachSchoolId"] });
      }
    }
  });

type RegisterForm = z.infer<typeof registerSchema>;

const defaultValues: RegisterForm = {
  role: undefined,
  schoolName: "",
  orgName: "",
  fullName: "",
  coachSchoolId: "",
  newSchoolName: "",
  newSchoolCity: "",
  phone: "",
  email: "",
  password: "",
  passwordConfirm: "",
};

export default function Register() {
  const { t, i18n } = useTranslation("auth");
  const [location, navigate] = useLocation();
  const utils = trpc.useUtils();
  const [selectedRole, setSelectedRole] = useState<RoleValue | null>(null);
  const [roleDialogOpen, setRoleDialogOpen] = useState(true);

  const registerMutation = trpc.auth.register.useMutation({
    onSuccess: data => {
      const nextUser = data.user
        ? {
          ...data.user,
          isCompetitionOrganizer: data.user.role === "organizer",
          school: data.user.role === "school"
            ? {
              name: data.user.name || "Школа",
              logoUrl: null,
              email: data.user.email ?? null,
            }
            : null,
        }
        : null;
      utils.auth.me.setData(undefined, nextUser as any);
      // Ответ login отдаёт «урезанного» пользователя: без linkedAccount и без
      // реального профиля школы (логотип/email). При staleTime=1000 эти данные
      // ещё секунду считаются свежими, поэтому кабинет успевал отрисоваться
      // без пункта «Перейти в кабинет организатора» — он появлялся только при
      // следующем переходе внутри кабинета. Помечаем me устаревшим сразу,
      // чтобы layout при монтировании дозапросил полный профиль.
      void utils.auth.me.invalidate();
      navigate("/dashboard");
    },
  });

  const resolver = useMemo(() => zodResolver(registerSchema), []);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
    watch,
  } = useForm<RegisterForm>({
    resolver,
    defaultValues,
  });

  // Профилактика дублей: пока человек печатает название школы, ищем уже
  // зарегистрированные школы с похожим названием. Только предупреждение.
  const schoolNameValue = watch("schoolName") || "";
  const [debouncedSchoolName, setDebouncedSchoolName] = useState("");
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSchoolName(schoolNameValue.trim()), 400);
    return () => clearTimeout(id);
  }, [schoolNameValue]);

  const schoolDuplicates = trpc.auth.checkSchoolName.useQuery(
    { name: debouncedSchoolName },
    { enabled: selectedRole === "school" && debouncedSchoolName.length >= 3 },
  );
  const duplicateMatches = schoolDuplicates.data?.matches ?? [];

  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const roleParam = params.get("role") as RoleValue | null;
    const roleFromPath = location.split("/")[2] as RoleValue | undefined;
    const candidate = roleValues.includes(roleParam as RoleValue)
      ? roleParam
      : roleValues.includes(roleFromPath as RoleValue)
        ? roleFromPath
        : null;
    if (candidate) {
      setSelectedRole(candidate);
      setValue("role", candidate);
      setRoleDialogOpen(false);
    } else {
      setSelectedRole(null);
      setRoleDialogOpen(true);
    }
  }, [location, setValue]);

  const handleRoleSelect = (role: RoleValue) => {
    setSelectedRole(role);
    setValue("role", role);
    setRoleDialogOpen(false);
  };

  const handleBack = () => {
    reset(defaultValues);
    setSelectedRole(null);
    setRoleDialogOpen(true);
  };

  const onSubmit = async (values: RegisterForm) => {
    if (!selectedRole) return;
    const payload: Record<string, any> = {
      role: selectedRole,
      email: values.email.trim(),
      phone: values.phone.trim(),
      password: values.password,
    };

    if (selectedRole === "school") {
      payload.schoolName = values.schoolName?.trim();
    }
    if (selectedRole === "organizer") {
      payload.orgName = values.orgName?.trim();
    }
    if (selectedRole === "coach") {
      payload.fullName = values.fullName?.trim();
      if (pickedSchoolId) payload.schoolId = pickedSchoolId;
      else {
        payload.newSchoolName = values.newSchoolName?.trim();
        payload.newSchoolCity = values.newSchoolCity?.trim() || undefined;
      }
    }

    await registerMutation.mutateAsync(payload as any);
  };

  const [schoolQuery, setSchoolQuery] = useState("");
  const [pickedSchoolId, setPickedSchoolId] = useState<number | null>(null);
  const [showNewSchool, setShowNewSchool] = useState(false);
  const schoolsQuery = trpc.auth.schoolsForSignup.useQuery(undefined, { enabled: selectedRole === "coach" });

  const phoneValue = watch("phone") ?? "";
  const phoneRegister = register("phone");

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-muted/50 to-background flex items-start justify-center px-4 py-8 overflow-y-auto">
      <LanguageSwitcher localOnly className="fixed top-4 right-4 z-50" />
      <Dialog
        open={roleDialogOpen}
        onOpenChange={(open) => {
          if (!selectedRole) {
            setRoleDialogOpen(true);
            return;
          }
          setRoleDialogOpen(open);
        }}
      >
        <DialogContent showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>{t("register.welcome")}</DialogTitle>
            <DialogDescription>{t("register.chooseRole")}</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3">
            {roleCards.map((role) => {
              const Icon = role.icon;
              return (
                <button
                  key={role.value}
                  type="button"
                  className="text-left rounded-lg border border-white/10 bg-muted/30 p-4 transition hover:border-white/30 hover:bg-muted/50"
                  onClick={() => handleRoleSelect(role.value)}
                >
                  <div className="flex items-start gap-3">
                    <div className="h-10 w-10 rounded-full bg-background/80 border border-white/10 flex items-center justify-center">
                      <Icon className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <div className="font-semibold">{t(`register.roles.${role.value}.title`)}</div>
                      <div className="text-xs text-muted-foreground">{t(`register.roles.${role.value}.description`)}</div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      <Card className="w-full max-w-2xl shadow-xl">
        <CardHeader className="space-y-2">
          <CardTitle className="text-xl sm:text-2xl">{t("register.title")}</CardTitle>
          <CardDescription>
            {selectedRole ? t("register.subtitleFilled") : t("register.subtitleChooseRole")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {selectedRole && (
            <form className="grid grid-cols-1 md:grid-cols-2 gap-6" onSubmit={handleSubmit(onSubmit)}>
              <input type="hidden" {...register("role")} />
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    {t("register.roleLabel")} <span className="text-foreground font-medium">{t(`register.roles.${selectedRole}.title`)}</span>
                  </p>
                  <Button type="button" variant="ghost" size="sm" onClick={handleBack}>
                    {t("register.back")}
                  </Button>
                </div>

                {selectedRole === "coach" && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="fullName">{t("register.coachFullNameLabel")}</Label>
                      <Input id="fullName" placeholder={t("register.coachFullNamePlaceholder")} {...register("fullName")} />
                      {errors.fullName && <p className="text-sm text-destructive">{t(errors.fullName.message!)}</p>}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="coachSchoolId">{t("register.coachSchoolLabel")}</Label>
                      <Input
                        placeholder={t("register.coachSchoolSearch")}
                        value={schoolQuery}
                        onChange={(e) => setSchoolQuery(e.target.value)}
                      />
                      <div className="max-h-44 overflow-y-auto rounded-md border border-border divide-y divide-border/60">
                        {(schoolsQuery.data || [])
                          .filter(sc => !schoolQuery.trim() || `${sc.name} ${sc.city ?? ""}`.toLowerCase().includes(schoolQuery.trim().toLowerCase()))
                          .map(sc => (
                            <button
                              key={sc.id}
                              type="button"
                              onClick={() => {
                                setPickedSchoolId(sc.id);
                                setShowNewSchool(false);
                                setValue("coachSchoolId", String(sc.id), { shouldValidate: true });
                                setValue("newSchoolName", "");
                                setSchoolQuery(sc.name);
                              }}
                              className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                                pickedSchoolId === sc.id ? "bg-primary/20 text-primary font-medium" : "hover:bg-muted/60"
                              }`}
                            >
                              {sc.name}
                              {sc.city ? <span className="text-muted-foreground"> · {sc.city}</span> : null}
                            </button>
                          ))}
                        {(schoolsQuery.data || []).filter(sc => !schoolQuery.trim() || `${sc.name} ${sc.city ?? ""}`.toLowerCase().includes(schoolQuery.trim().toLowerCase())).length === 0 && (
                          <div className="px-3 py-2 text-sm text-muted-foreground">{t("register.coachSchoolEmpty")}</div>
                        )}
                      </div>
                      <p className="text-[11px] text-muted-foreground">
                        {t("register.coachSchoolCount", { count: (schoolsQuery.data || []).length })}
                      </p>
                      {pickedSchoolId && (
                        <p className="text-sm text-primary">
                          {t("register.coachSchoolPicked", { name: (schoolsQuery.data || []).find(x => x.id === pickedSchoolId)?.name || "" })}
                          <button
                            type="button"
                            className="ml-2 underline underline-offset-2 text-muted-foreground"
                            onClick={() => { setPickedSchoolId(null); setValue("coachSchoolId", ""); setSchoolQuery(""); }}
                          >
                            {t("register.coachSchoolReset")}
                          </button>
                        </p>
                      )}
                      {errors.coachSchoolId && <p className="text-sm text-destructive">{t(errors.coachSchoolId.message!)}</p>}

                      <button
                        type="button"
                        className="text-sm text-primary underline underline-offset-2"
                        onClick={() => { setPickedSchoolId(null); setValue("coachSchoolId", ""); setShowNewSchool(v => !v); }}
                      >
                        {t("register.coachSchoolMissing")}
                      </button>

                      {showNewSchool && (
                        <div className="space-y-2 rounded-md border border-dashed border-border p-3">
                          <Input placeholder={t("register.newSchoolNamePlaceholder")} {...register("newSchoolName")} />
                          <Input placeholder={t("register.newSchoolCityPlaceholder")} {...register("newSchoolCity")} />
                          <p className="text-[11px] text-muted-foreground">{t("register.newSchoolHint")}</p>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {selectedRole === "school" && (
                  <div className="space-y-2">
                    <Label htmlFor="schoolName">{t("register.schoolNameLabel")}</Label>
                    <Input id="schoolName" placeholder={t("register.schoolNamePlaceholder")} {...register("schoolName")} />
                    {errors.schoolName && <p className="text-sm text-destructive">{t(errors.schoolName.message!)}</p>}

                    {duplicateMatches.length > 0 && (
                      <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 space-y-2">
                        <p className="text-sm font-medium text-amber-300">{t("register.dupTitle")}</p>
                        <ul className="space-y-1">
                          {duplicateMatches.map((m, idx) => (
                            <li key={idx} className="text-sm text-amber-300">
                              <span className="font-medium">{m.name}</span>
                              <span className="text-amber-300/80">
                                {" · "}
                                {m.city || t("register.dupNoCity")}
                                {" · "}
                                {t("register.dupRegisteredAt", {
                                  date: new Date(m.createdAt).toLocaleDateString(i18n.language),
                                })}
                              </span>
                            </li>
                          ))}
                        </ul>
                        <p className="text-xs text-amber-300/90">{t("register.dupCoachHint")}</p>
                        <p className="text-xs text-amber-300/90">
                          <Trans
                            i18nKey="register.dupHeadHint"
                            ns="auth"
                            components={{
                              a: <a className="underline font-medium" href={`mailto:${SUPPORT_EMAIL}`} />,
                            }}
                          />
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {selectedRole === "organizer" && (
                  <div className="space-y-2">
                    <Label htmlFor="orgName">{t("register.orgNameLabel")}</Label>
                    <Input id="orgName" placeholder={t("register.orgNamePlaceholder")} {...register("orgName")} />
                    {errors.orgName && <p className="text-sm text-destructive">{t(errors.orgName.message!)}</p>}
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="phone">{t("register.phoneLabel")}</Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="+7 (777) 123-45-67"
                    value={phoneValue}
                    onChange={(e) => {
                      setValue("phone", e.target.value, { shouldDirty: true, shouldValidate: true });
                      phoneRegister.onChange(e);
                    }}
                    onBlur={phoneRegister.onBlur}
                    name={phoneRegister.name}
                    ref={phoneRegister.ref}
                  />
                  {errors.phone && <p className="text-sm text-destructive">{t(errors.phone.message!)}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">{t("register.emailLabel")}</Label>
                  <Input id="email" type="email" placeholder="you@example.com" {...register("email")} />
                  {errors.email && <p className="text-sm text-destructive">{t(errors.email.message!)}</p>}
                </div>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="password">{t("register.passwordLabel")}</Label>
                  <Input id="password" type="password" placeholder="••••••••" {...register("password")} />
                  {errors.password && <p className="text-sm text-destructive">{t(errors.password.message!)}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="passwordConfirm">{t("register.passwordConfirmLabel")}</Label>
                  <Input
                    id="passwordConfirm"
                    type="password"
                    placeholder="••••••••"
                    {...register("passwordConfirm")}
                  />
                  {errors.passwordConfirm && (
                    <p className="text-sm text-destructive">{t(errors.passwordConfirm.message!)}</p>
                  )}
                </div>

                {registerMutation.error && (
                  <p className="text-sm text-destructive">{registerMutation.error.message}</p>
                )}

                <Button type="submit" className="w-full" disabled={registerMutation.isPending}>
                  {registerMutation.isPending ? t("register.submitting") : t("register.submit")}
                </Button>
                <p className="text-sm text-muted-foreground text-center">
                  {t("register.haveAccount")}{" "}
                  <Link href="/login" className="text-primary hover:underline">
                    {t("register.loginLink")}
                  </Link>
                </p>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
